// src/modules/study-chat/api.ts
import { supabase as defaultClient } from '@/lib/supabase';
import type { StudyGroup, Member, Message } from './types';

export type StudyChatApiOpts = { client?: typeof defaultClient };

/** Friendly initial display name derived from auth (no dependency on user_metadata.display_name) */
export function getInitialDisplayName(user?: any): string {
  return (
    user?.user_metadata?.full_name ??
    user?.email?.split?.('@')?.[0] ??
    ''
  ).slice(0, 30);
}

/** Should we prompt for a better display name? (empty or just the email prefix) */
export function shouldPromptForDisplayName(user?: any): boolean {
  if (!user) return true;
  const initial = getInitialDisplayName(user);
  const emailPrefix = user?.email?.split?.('@')?.[0] ?? '';
  return !initial || (!!emailPrefix && initial === emailPrefix);
}

function getClient(opts: StudyChatApiOpts) {
  return opts.client ?? defaultClient;
}

async function getCurrentUserId(sb: typeof defaultClient) {
  const { data: u, error } = await sb.auth.getUser();
  if (error) throw error;
  return u?.user?.id ?? null;
}

export function studyChatApi(opts: StudyChatApiOpts = {}) {
  const sb = getClient(opts);

  // Helper: ensure there is a membership row for current user in this group.
  // Uses UPSERT on (group_id, user_id) to avoid duplicate errors.
  async function upsertMembership(group_id: number, display_name: string, role: 'member' | 'owner' = 'member') {
    const uid = await getCurrentUserId(sb);
    if (!uid) throw new Error('Sign in required.');

    const payload: Partial<Member> = {
      group_id,
      user_id: uid,
      display_name: (display_name ?? '').trim().slice(0, 30) || 'Member',
      role,
    } as any;

    const { data, error } = await sb
      .from('study_group_members')
      .upsert(payload, { onConflict: 'group_id,user_id' })
      .select('*')
      .single();

    if (error) throw error;
    return data as Member;
  }

  // Helper: read current member display_name for posting
  async function getMemberDisplayName(group_id: number) {
    const uid = await getCurrentUserId(sb);
    if (!uid) throw new Error('Sign in required.');
    const { data, error } = await sb
      .from('study_group_members')
      .select('display_name')
      .eq('group_id', group_id)
      .eq('user_id', uid)
      .maybeSingle();
    if (error) throw error;
    return data?.display_name || 'Member';
  }

  async function isMember(groupId: number) {
    // relies on RLS: user only sees their own membership row
    const { count, error } = await sb
      .from('study_group_members')
      .select('id', { head: true, count: 'exact' })
      .eq('group_id', groupId);
    if (error) throw error;
    return (count ?? 0) > 0;
  }

  async function getMyDisplayName(group_id: number) {
    // Uses RLS to read only the caller's membership row
    const uid = await getCurrentUserId(sb);
    if (!uid) throw new Error('Sign in required.');
    const { data, error } = await sb
      .from('study_group_members')
      .select('display_name')
      .eq('group_id', group_id)
      .eq('user_id', uid)
      .maybeSingle();
    if (error) throw error;
    return data?.display_name || '';
  }
  
  async function setMemberDisplayName(group_id: number, display_name: string) {
    // Upsert membership with the new name; safe for both first-time and updates
    return await upsertMembership(
      group_id,
      (display_name ?? '').trim().slice(0, 30) || 'Member',
      'member'
    );
  }

  return {
    async listGroups(subjectId?: number) {
      const q = sb
        .from('study_groups')
        .select('*')
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true });

      // RLS scopes by site automatically; subjectId narrows within site
      const { data, error } = subjectId ? await q.eq('subject_id', subjectId) : await q;
      if (error) throw error;
      return (data ?? []) as StudyGroup[];
    },

    // Accept optional creator_display_name so the creator is recorded with the chosen name
    async createGroup(input: {
      subject_id?: number;
      title: string;
      max_members?: number;
      is_default?: boolean;
      description?: string;
      creator_display_name?: string;
    }) {
      const uid = await getCurrentUserId(sb);
      if (!uid) throw new Error('You must be signed in to create a study group.');
      if (!input.subject_id) throw new Error('Missing subject_id.');

      // Friendly pre-check: one group per subject per creator (the DB also enforces via unique index)
      {
        const { data: existing, error: exErr } = await sb
          .from('study_groups')
          .select('id')
          .eq('subject_id', input.subject_id)
          .eq('created_by', uid)
          .limit(1);

        if (exErr) throw exErr;
        if (existing && existing.length > 0) {
          throw new Error('You already created a group for this subject.');
        }
      }

      // Create group (site_id is populated by DB trigger from subjects)
      const { data, error } = await sb
        .from('study_groups')
        .insert({
          subject_id: input.subject_id,
          title: input.title?.trim(),
          max_members: input.max_members ?? 200,
          is_default: input.is_default ?? false,
          description: input.description ?? null,
          created_by: uid,
        })
        .select('*')
        .single();

      if (error) throw error;

      // Ensure creator is a member with 'owner' role (UPSERT in case of retries)
      const creatorName =
        (input.creator_display_name ?? '').trim().slice(0, 30) ||
        // fallback to auth-friendly name (not persisted dependency)
        (await (async () => {
          const { data: u } = await sb.auth.getUser();
          return getInitialDisplayName(u?.user);
        })()) ||
        'Owner';

      await upsertMembership(data.id, creatorName, 'owner');

      return data as StudyGroup;
    },

    async joinGroup(group_id: number, display_name: string) {
      // Upsert to avoid duplicates if user already joined
      const member = await upsertMembership(group_id, display_name, 'member');
      return member;
    },

    async getMyDisplayName(group_id: number) {
      // Reuse private helper through a thin wrapper
      return await getMemberDisplayName(group_id);
    },

    async setMemberDisplayName(group_id: number, display_name: string) {
      return await setMemberDisplayName(group_id, display_name);
    },
    
    async listMembers(group_id: number) {
      const { data, error } = await sb
        .from('study_group_members')
        .select('*')
        .eq('group_id', group_id)
        .order('joined_at', { ascending: true });

      if (error) throw error;
      return (data ?? []) as Member[];
    },

    async postMessage(group_id: number, body: string) {
      const uid = await getCurrentUserId(sb);
      if (!uid) throw new Error('You must be signed in to send messages.');

      // Use the member’s current group display_name (keeps messages consistent with membership)
      const display_name = await getMemberDisplayName(group_id);

      const { data, error } = await sb
        .from('study_group_messages')
        .insert({ group_id, body, display_name /* user_id is set by trigger */ })
        .select('*')
        .single();

      if (error) throw error;
      return data as Message;
    },

    async fetchMessages(group_id: number, limit = 100) {
      const { data, error } = await sb
        .from('study_group_messages_visible')
        .select('*')
        .eq('group_id', group_id)
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return (data ?? []) as Message[];
    },

    async fetchGroupMeta(group_id: number) {
      const { data, error } = await sb
        .from('study_groups')
        .select('id, cleared_at, created_by')
        .eq('id', group_id)
        .single();

      if (error) throw error;
      return data as Pick<StudyGroup, 'id' | 'cleared_at' | 'created_by'>;
    },

    async clearMessages(group_id: number) {
      const nowIso = new Date().toISOString();
      const { data, error } = await sb
        .from('study_groups')
        .update({ cleared_at: nowIso })
        .eq('id', group_id)
        .select('id, cleared_at');

      if (error) throw error;

      if (!data || data.length === 0) {
        return { id: group_id, cleared_at: nowIso } as Pick<StudyGroup, 'id' | 'cleared_at'>;
      }
      return data[0] as Pick<StudyGroup, 'id' | 'cleared_at'>;
    },
  };
}
